/* istanbul ignore file */

import { Module } from 'vuex';
import { AppState } from '..';

import { MBV2ItemError, MBV2NutritionSummary, MBV2NutritionSummaryDetails, MBV2SectionError, MBV2SelectedItem, MonoMenuTopLevelGroup } from '@/models';
import { prefixValues } from '@/helpers/util';
import Vue from 'vue';
import { emptyNutritionData } from '@/services/mono-menu/mono-menu-constants';

// This is the meal builder used in nutrition calc.
// Maybe if it works really well we can port it to main meal builder later

const MUTATION_NAMES = {
  SET_TOP_LEVEL_MENU_ITEM: 'setTopLevelMenuItem',
  SET_ENTREE_ID: 'setEntreeId',
  SET_SELECTED_ITEM: 'setSelectedItem',
  CLEAR_SELECTED_ITEMS: 'clearSelectedItems',
  REMOVE_SELECTED_ITEM: 'removeSelectedItem',
  SET_CUSTOMIZING_ITEM: 'setCustomizingItem',
  SET_MEAL_NAME: 'setMealName',
  SET_FULL_STATE: 'setFullState',
  CLEAR_FULL_STATE: 'clearFullState',
  SET_ITEM_ERROR: 'setItemError',
  REMOVE_ITEM_ERROR: 'removeItemError',
  SET_SECTION_ERROR: 'setSectionError',
  REMOVE_SECTION_ERROR: 'removeSectionError',
  SET_ORDER_BUTTON_ERROR: 'setOrderButtonError',
  REMOVE_ORDER_BUTTON_ERROR: 'removeOrderButtonError',
  CLEAR_ALL_ERRORS: 'clearAllErrors',
  SET_NUTRITION: 'setNutrition',
  ADD_OPTED_OUT_DEFAULT_ITEM: 'addOptedOutDefaultItem'
};

export const MUTATIONS = prefixValues(MUTATION_NAMES, 'mealBuilderV2/');

export interface MealBuilderV2ModuleState {
  topLevelMenuItem: MonoMenuTopLevelGroup | null;
  entreeId: string | null; // the menu being displayed
  selectedItems: { [index: string]: MBV2SelectedItem };
  mealId: string | null;
  mealName: string | null;
  itemErrors: { [index: string]: string };
  sectionErrors: { [index: string]: string };
  orderButtonError: string | null;
  nutrition: MBV2NutritionSummary | MBV2NutritionSummaryDetails;
  optedOutDefaultItemIds: string[];
}

function emptyState() {
  return {
    topLevelMenuItem: null,
    entreeId: null,
    selectedItems: {},
    mealId: null,
    mealName: null,
    itemErrors: {},
    sectionErrors: {},
    orderButtonError: null,
    nutrition: Object.assign({}, emptyNutritionData),
    optedOutDefaultItemIds: []
  };
}

export function makeMealBuilderV2Module(): Module<MealBuilderV2ModuleState, AppState> {
  return {
    namespaced: true,
    state: Object.assign({}, emptyState()),
    mutations: {
      [MUTATION_NAMES.SET_TOP_LEVEL_MENU_ITEM](state, topLevelMenuItem: MonoMenuTopLevelGroup) {
        state.topLevelMenuItem = topLevelMenuItem;
      },
      [MUTATION_NAMES.SET_ENTREE_ID](state, entreeId: any) {
        state.entreeId = entreeId;
      },
      [MUTATION_NAMES.SET_SELECTED_ITEM](state: MealBuilderV2ModuleState, selectedItem: MBV2SelectedItem) {
        Vue.set(state.selectedItems, selectedItem.itemId, selectedItem);
      },
      [MUTATION_NAMES.CLEAR_SELECTED_ITEMS](state) {
        state.selectedItems = {};
      },
      [MUTATION_NAMES.REMOVE_SELECTED_ITEM](state, itemId: string) {
        Vue.delete(state.selectedItems, itemId);
      },
      [MUTATION_NAMES.SET_MEAL_NAME](state, mealName: string) {
        state.mealName = mealName;
      },
      [MUTATION_NAMES.SET_FULL_STATE](state, fullMBv2State: MealBuilderV2ModuleState) {
        Object.keys(fullMBv2State).forEach(key => Vue.set(state, key, fullMBv2State[key as keyof MealBuilderV2ModuleState]));
        const individualAssignments: (keyof MealBuilderV2ModuleState)[] = ['selectedItems', 'itemErrors', 'sectionErrors'];
        individualAssignments.forEach((objectKey)=> {
          const newStateObj = fullMBv2State[objectKey] as { [index: string]: any }
          Object.keys(fullMBv2State[objectKey] as Object).forEach(key => {
            Vue.set(state[objectKey] as Object, key, newStateObj[key]);
          })
        })
      },
      [MUTATION_NAMES.CLEAR_FULL_STATE](state) {
        Object.assign(state, emptyState());
      },
      [MUTATION_NAMES.SET_ITEM_ERROR](state, error: MBV2ItemError) {
        Vue.set(state.itemErrors, error.itemId, error.errorText);
      },
      [MUTATION_NAMES.REMOVE_ITEM_ERROR](state, itemId: string) {
        Vue.delete(state.itemErrors, itemId);
      },
      [MUTATION_NAMES.SET_SECTION_ERROR](state, error: MBV2SectionError) {
        Vue.set(state.sectionErrors, error.contentGroupName, error.errorText);
      },
      [MUTATION_NAMES.REMOVE_SECTION_ERROR](state, contentGroupName: string) {
        Vue.delete(state.sectionErrors, contentGroupName);
      },
      [MUTATION_NAMES.SET_ORDER_BUTTON_ERROR](state, orderButtonError: string) {
        state.orderButtonError = orderButtonError;
      },
      [MUTATION_NAMES.REMOVE_ORDER_BUTTON_ERROR](state) {
        state.orderButtonError = null;
      },
      [MUTATION_NAMES.CLEAR_ALL_ERRORS](state) {
        state.itemErrors = {};
        state.sectionErrors = {};
        state.orderButtonError = null;
      },
      [MUTATION_NAMES.SET_NUTRITION](state, nutrition) {
        state.nutrition = nutrition;
      },
      [MUTATION_NAMES.ADD_OPTED_OUT_DEFAULT_ITEM](state, itemId: string) {
        state.optedOutDefaultItemIds.push(itemId);
      },
    },
  };
}
